/* ---------------------------------------------------------------------------
   The ground around a structure, as three.js objects: the land within a radius,
   a pale surface with its contour lines at their elevations.

   ONE BUILDER FOR BOTH VIEWS THAT DRAW IT — the record's 3D model
   (src/modeler/meshView.ts), which lays it under the model in the model's own
   frame, and the base-map page's 3D (src/basemap/view3d.ts), which draws it
   north-up with the structure stood on it. The arithmetic is ./isolate.ts and
   ./terrain.ts; this only turns it into a Group.

   TWO OPTIONS ON TOP OF THE SURFACE, each a switch on the returned Ground:
   a WIREFRAME of the land — its triangle mesh, a hundred-odd triangles a side,
   drawn instead of the solid surface — and the ROADS AND RIVERS of this
   deployment's own base map (./features3d.ts), laid on the ground. Both are
   built the first time they are asked for and kept.

   ROADS AND RIVERS ARE SURFACES, NOT LINES. Every road is a two-lane
   carriageway, 2 x 3.65 m, with its centre line drawn over it; every river and
   stream a water surface along its centre line. Both widths are ASSUMED - the
   base map carries a road's class but not its lanes, and a river's course but
   not its width - so the water is a simulation of where the river runs, not of
   how wide it is. The wide rivers' mapped banks stay as lines, being the one
   measured width.

   THE WATER IS IN ITS CHANNEL. Each river and stream is sunk below its banks,
   never rising downstream, with its banks drawn as surfaces up to the land —
   and the land, its contours and its wireframe are not drawn where a channel
   is (./isolate.ts, bankGeometry): a mask of the channels' footprint, read by
   their shaders, while the roads and rivers are shown.

   THE ROADS ARE ON EMBANKMENTS, and stop at the water: built up a metre or two
   with their side slopes down to the ground, and cut where they would cross a
   river, a stream or a mapped water area — the structure spans that gap, or
   nothing does. A road OSM marks as a bridge is not cut, and runs straight
   between its ends. The heights of both are assumed, like the widths.

   In the group's frame X is east, Z is south, Y is up, the origin is the ground
   under the structure and heights are relative to it — see ./terrain.ts.

   THREE.JS IS PASSED IN, never imported, so this module costs the register
   bundle nothing until a view that has already loaded it calls in.
   --------------------------------------------------------------------------- */

import { loadTerrain, terrainGeometry, toLocal } from './terrain.ts';
import {
  contourInterval,
  contourSegments,
  despike,
  discIndices,
  bankGeometry,
  channelProfile,
  drapeLine,
  embankedRoads,
  stripGeometry,
  type Channel,
  type RoadLine,
  heightAt,
  isolatePatch,
  footprintMask,
  wetTest,
  wireframeSegments,
} from './isolate.ts';
import { loadSite, type FeatureKind } from './features3d.ts';

type Three = typeof import('three');

/** The ground's colours, per backdrop: a pale surface the lines read on. */
const GROUND = {
  light: { surface: 0xe4ddcf, minor: 0xb08a63, index: 0x7a5636, wire: 0xd3cdc3 },
  dark: { surface: 0x2c2a26, minor: 0x6e5c48, index: 0xa58a6a, wire: 0x3a3833 },
};

/** Roads and water, in the 2D map's own colours as far as one-pixel lines allow. */
const LINES: Record<'light' | 'dark', Record<FeatureKind, number>> = {
  light: { major: 0xe09a2a, minor: 0xd8d8d8, river: 0x2f78bd, stream: 0x4f8fc4, water: 0x2f78bd },
  dark: { major: 0xd19a3c, minor: 0xb8bcc2, river: 0x5fa3dc, stream: 0x4b86b8, water: 0x5fa3dc },
};
const KINDS: FeatureKind[] = ['water', 'stream', 'river', 'minor', 'major'];

/** Two lanes of 3.65 m - assumed for every road; see the header. */
const ROAD_WIDTH = 7.3;
/** Simulated watercourses, metres: width, water below the banks, and how far a DEM low may trench. */
const RIVER = { width: 20, drop: 2.5, maxDig: 6 };
const STREAM = { width: 5, drop: 1, maxDig: 3 };
/** Assumed fill under a road's crown, metres. */
const EMBANKMENT = { major: 1.5, minor: 0.8 };
/** How far short of the water a road stops, beyond the water's edge. */
const ROAD_CLEAR = 4;
const SURFACES = {
  light: { asphalt: 0x55585e, water: 0x4a8fd0, fill: 0xb3a07c, bank: 0x9a8a6c },
  dark: { asphalt: 0x3a3d42, water: 0x2f6fae, fill: 0x4d4537, bank: 0x423b30 },
};

export interface Ground {
  group: import('three').Group;
  radius: number;
  /** Relief within the disc, metres, after the DEM's voids are out. */
  min: number;
  max: number;
  /** Contour interval used, metres. */
  interval: number;
  setTheme(theme: string): void;
  /** The land as its triangle mesh instead of a solid surface. */
  setWireframe(on: boolean): void;
  /** Spacing of that mesh's corners, metres. */
  meshSpacing: number;
  /** Ground height at a point of the group's frame (X east, Z south), relative to its centre. */
  heightAt(x: number, z: number): number;
  /**
   * The roads and rivers of the base map `region` laid on the ground — fetched
   * the first time. Resolves with how many lines were drawn.
   */
  setFeatures(on: boolean, region: string): Promise<number>;
  dispose(): void;
}

/** Fetch the DEM under a disc of `radius` metres and build its ground. */
export async function buildGround(
  THREE: Three,
  at: { lat: number; lng: number },
  radius: number,
  theme: string,
): Promise<Ground> {
  const patch = isolatePatch(at.lat, at.lng, radius);
  const land = await loadTerrain(patch);

  /* The DEM's voids out first, or one pit kilometres deep is the whole picture. */
  const relief = despike(land.heights, patch.samples);
  const { positions, indices } = terrainGeometry(patch, land.heights);
  const surfaceGeom = new THREE.BufferGeometry();
  surfaceGeom.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  surfaceGeom.setIndex(new THREE.BufferAttribute(discIndices(positions, indices, radius), 1));
  surfaceGeom.computeVertexNormals();

  /* The channels' footprint, which the land, its contours and its mesh leave
     out while the rivers are shown: a texture over the patch, read by their
     shaders. Empty, and off, until the rivers are first drawn. */
  const mask = {
    channelMask: { value: null as import('three').Texture | null },
    channelMaskOn: { value: 0 },
    channelSpan: { value: patch.span },
  };
  const masked = <M extends import('three').Material>(mat: M): M => {
    mat.onBeforeCompile = (shader) => {
      Object.assign(shader.uniforms, mask);
      shader.vertexShader =
        'varying vec2 vChannelXZ;\n' +
        shader.vertexShader.replace('#include <begin_vertex>', '#include <begin_vertex>\n  vChannelXZ = position.xz;');
      shader.fragmentShader =
        'uniform sampler2D channelMask;\nuniform float channelMaskOn;\nuniform float channelSpan;\nvarying vec2 vChannelXZ;\n' +
        shader.fragmentShader.replace(
          'void main() {',
          'void main() {\n  if (channelMaskOn > 0.5 && texture2D(channelMask, vChannelXZ / channelSpan + 0.5).r > 0.5) discard;',
        );
    };
    return mat;
  };

  const colours = GROUND[theme === 'dark' ? 'dark' : 'light'];
  const surfaceMat = masked(
    new THREE.MeshStandardMaterial({
      color: colours.surface,
      roughness: 1,
      metalness: 0,
      side: THREE.DoubleSide,
      /* Pushed back a hair so the contour lines lying on it always win the depth test. */
      polygonOffset: true,
      polygonOffsetFactor: 1,
      polygonOffsetUnits: 1,
    }),
  );

  const interval = contourInterval(relief.max - relief.min);
  const lines = contourSegments(patch, land.heights, land.centreElevation, interval, radius);
  const lineSet = (data: Float32Array, color: number): import('three').LineSegments => {
    const geom = new THREE.BufferGeometry();
    geom.setAttribute('position', new THREE.BufferAttribute(data, 3));
    return new THREE.LineSegments(geom, masked(new THREE.LineBasicMaterial({ color })));
  };
  const minor = lineSet(lines.minor, colours.minor);
  const index = lineSet(lines.index, colours.index);

  const surface = new THREE.Mesh(surfaceGeom, surfaceMat);
  const group = new THREE.Group();
  group.name = 'ground';
  group.add(surface, minor, index);

  let current = theme === 'dark' ? 'dark' : 'light';
  let wire: import('three').LineSegments | null = null;
  let features: import('three').Group | null = null;
  let featureCount = 0;
  let loadingFeatures: Promise<void> | null = null;
  const featureMats = new Map<FeatureKind, import('three').LineBasicMaterial>();
  let asphaltMat: import('three').MeshStandardMaterial | null = null;
  let waterMat: import('three').MeshStandardMaterial | null = null;
  let fillMat: import('three').MeshStandardMaterial | null = null;
  let bankMat: import('three').MeshStandardMaterial | null = null;

  const every = Math.max(1, Math.round((patch.samples - 1) / 100));

  /* What a line on the ground is lifted by, so it is never inside the surface. */
  const lift = Math.max(1, radius / 2500);
  const groundAt = (x: number, z: number) => heightAt(patch, land.heights, x, z);
  const step = patch.span / (patch.samples - 1);

  return {
    group,
    radius,
    min: relief.min,
    max: relief.max,
    interval,
    setTheme(next) {
      current = next === 'dark' ? 'dark' : 'light';
      const c = GROUND[current as 'light' | 'dark'];
      surfaceMat.color.setHex(c.surface);
      (minor.material as import('three').LineBasicMaterial).color.setHex(c.minor);
      (index.material as import('three').LineBasicMaterial).color.setHex(c.index);
      if (wire) (wire.material as import('three').LineBasicMaterial).color.setHex(c.wire);
      for (const [kind, mat] of featureMats) mat.color.setHex(LINES[current as 'light' | 'dark'][kind]);
      const s = SURFACES[current as 'light' | 'dark'];
      asphaltMat?.color.setHex(s.asphalt);
      waterMat?.color.setHex(s.water);
      fillMat?.color.setHex(s.fill);
      bankMat?.color.setHex(s.bank);
    },
    meshSpacing: Math.round((patch.span / (patch.samples - 1)) * every),
    heightAt: groundAt,
    setWireframe(on) {
      if (on && !wire) {
        const geom = new THREE.BufferGeometry();
        geom.setAttribute('position', new THREE.BufferAttribute(wireframeSegments(patch, land.heights, radius, every), 3));
        wire = new THREE.LineSegments(geom, masked(new THREE.LineBasicMaterial({ color: GROUND[current as 'light' | 'dark'].wire })));
        group.add(wire);
      }
      if (wire) wire.visible = on;
      surface.visible = !on;
    },
    async setFeatures(on, region) {
      if (on && !features) {
        loadingFeatures ??= (async () => {
          const site = await loadSite(region, at, radius);
          const local = (coords: [number, number][]) =>
            coords.map(([lng, lat]) => {
              const l = toLocal(patch, lat, lng);
              return [l.x, l.z] as [number, number];
            });
          const byKind = new Map<FeatureKind, number[]>(KINDS.map((k) => [k, []]));
          const roads: RoadLine[] = [];
          const channels: Channel[] = [];
          const r2 = radius * radius;
          for (const line of site.lines) {
            const points = local(line.coords);
            if (line.kind === 'major' || line.kind === 'minor') {
              roads.push({ points, bridge: line.bridge, height: EMBANKMENT[line.kind] });
            } else if (line.kind === 'river' || line.kind === 'stream') {
              const spec = line.kind === 'river' ? RIVER : STREAM;
              const profile = channelProfile(points, groundAt, Math.min(step, 10), spec.drop, spec.maxDig);
              channels.push({ ...profile, width: spec.width });
              /* The course drawn on its own water, so it reads from afar. */
              const out = byKind.get(line.kind)!;
              for (let i = 1; i < profile.points.length; i++) {
                const [ax, az] = profile.points[i - 1]!;
                const [bx, bz] = profile.points[i]!;
                if (ax * ax + az * az > r2 || bx * bx + bz * bz > r2) continue;
                out.push(ax, profile.water[i - 1]! + 0.1, az, bx, profile.water[i]! + 0.1, bz);
              }
            } else drapeLine(points, groundAt, step, radius, lift * 0.6, byKind.get('water')!);
          }

          const g = new THREE.Group();
          g.name = 'roads-and-water';
          const surfaceStrip = (
            strip: { positions: Float32Array; indices: Uint32Array },
            mat: import('three').Material,
            order: number,
          ): void => {
            if (!strip.indices.length) return;
            const geom = new THREE.BufferGeometry();
            geom.setAttribute('position', new THREE.BufferAttribute(strip.positions, 3));
            geom.setIndex(new THREE.BufferAttribute(strip.indices, 1));
            geom.computeVertexNormals();
            const mesh = new THREE.Mesh(geom, mat);
            mesh.renderOrder = order;
            g.add(mesh);
            featureCount += strip.indices.length / 6;
          };
          const colours = SURFACES[current as 'light' | 'dark'];
          waterMat = new THREE.MeshStandardMaterial({
            color: colours.water,
            roughness: 0.25,
            metalness: 0.1,
            side: THREE.DoubleSide,
            /* Leaving no depth behind, so the line of its course always shows on
               it, and two waters meeting at a confluence read as one. */
            depthWrite: false,
            polygonOffset: true,
            polygonOffsetFactor: -1,
            polygonOffsetUnits: -2,
          });
          asphaltMat = new THREE.MeshStandardMaterial({
            color: colours.asphalt,
            roughness: 0.9,
            metalness: 0,
            side: THREE.DoubleSide,
            polygonOffset: true,
            polygonOffsetFactor: -2,
            polygonOffsetUnits: -4,
          });
          fillMat = new THREE.MeshStandardMaterial({ color: colours.fill, roughness: 1, metalness: 0, side: THREE.DoubleSide });
          bankMat = new THREE.MeshStandardMaterial({ color: colours.bank, roughness: 1, metalness: 0, side: THREE.DoubleSide });

          /* The banks, and the land left out where the channels are. About a
             metre and a half a texel, as far as 4096 a side allows. */
          const size = Math.min(4096, Math.max(512, Math.ceil(patch.span / 1.5)));
          const texel = patch.span / size;
          const banks = bankGeometry(channels, groundAt, radius, texel * 0.8);
          surfaceStrip(banks, bankMat, 1);
          if (banks.footprint.length) {
            const canvas = document.createElement('canvas');
            canvas.width = canvas.height = size;
            const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
            const data = footprintMask(banks.footprint, patch.span, size, ctx);
            canvas.width = canvas.height = 0;
            const tex = new THREE.DataTexture(data, size, size, THREE.RedFormat, THREE.UnsignedByteType);
            tex.magFilter = THREE.LinearFilter;
            tex.minFilter = THREE.LinearFilter;
            tex.generateMipmaps = false;
            tex.unpackAlignment = 1;
            tex.needsUpdate = true;
            mask.channelMask.value = tex;
          }
          for (const spec of [RIVER, STREAM]) {
            const strips = channels
              .filter((c) => c.width === spec.width)
              .map((c) => ({ points: c.points, heights: c.water, bridge: false, level: true }));
            surfaceStrip(stripGeometry(strips, groundAt, spec.width, step, radius, 0), waterMat, 1);
          }
          const wet = wetTest(
            channels.map((c) => ({ points: c.points, width: c.width })),
            site.areas.map((a) => a.rings.map(local)),
            ROAD_CLEAR,
          );
          const built = embankedRoads(roads, groundAt, groundAt, wet, ROAD_WIDTH, step, radius);
          surfaceStrip(built.fill, fillMat, 2);
          surfaceStrip(built.deck, asphaltMat, 2);
          /* The centre line, raised clear of the asphalt: a line takes no depth
             offset, and the carriageway's pulls it forward. */
          for (let k = 1; k < built.centre.length; k += 3) built.centre[k]! += 0.6;
          byKind.get('major')!.push(...built.centre);
          for (const kind of KINDS) {
            const data = byKind.get(kind)!;
            if (!data.length) continue;
            const geom = new THREE.BufferGeometry();
            geom.setAttribute('position', new THREE.Float32BufferAttribute(data, 3));
            const mat = new THREE.LineBasicMaterial({ color: LINES[current as 'light' | 'dark'][kind] });
            featureMats.set(kind, mat);
            const seg = new THREE.LineSegments(geom, mat);
            seg.renderOrder = kind === 'major' ? 3 : 2;
            g.add(seg);
            featureCount += data.length / 6;
          }
          features = g;
          group.add(g);
        })();
        await loadingFeatures;
      }
      if (features) features.visible = on;
      mask.channelMaskOn.value = on && mask.channelMask.value ? 1 : 0;
      return featureCount;
    },
    dispose() {
      group.removeFromParent();
      group.traverse((node) => {
        const obj = node as import('three').Mesh;
        obj.geometry?.dispose();
        (obj.material as import('three').Material | undefined)?.dispose();
      });
      mask.channelMask.value?.dispose();
    },
  };
}
